/**
 * The article card (`show_article`, P5-6): a link to a page of the workspace's knowledge, with nothing to answer. It arrives as
 * a `workflow_step` action with `kind: "article"`; a message has one card to answer, so articles are kept apart on
 * `ChatMessage.articles` (shown above that card, in the order they arrived).
 */
import type { ChatAction } from "../types";
import type { WorkflowArticle } from "./contract";

export interface ArticleCard {
  run_id: string;
  step_id: string;
  article: WorkflowArticle;
}

/** The article an action carries, or null when it is anything else (a card to answer, a booking, a handoff ...). */
export function toArticleCard(action: ChatAction | null | undefined): ArticleCard | null {
  if (action?.type !== "workflow_step") return null;
  const { kind, article, run_id, step_id } = action.payload;
  return kind === "article" && article ? { run_id, step_id, article } : null;
}

/** The article cards among decoded actions, in order. */
export function articleCardsOf(actions: readonly (ChatAction | null | undefined)[]): ArticleCard[] {
  return actions.flatMap((action) => {
    const card = toArticleCard(action);
    return card ? [card] : [];
  });
}

/** The action that is left to answer once the articles are taken out (the first card that is not an article). */
export function answerableAction(actions: readonly (ChatAction | null | undefined)[]): ChatAction | undefined {
  return actions.find((action): action is ChatAction => action != null && toArticleCard(action) === null);
}

/** A message with the article added (the same card twice - a re-sent stream - is shown once). */
export function withArticle<T extends { articles?: ArticleCard[] }>(message: T, card: ArticleCard): T {
  const known = message.articles ?? [];
  if (known.some((item) => item.run_id === card.run_id && item.step_id === card.step_id)) return message;
  return { ...message, articles: [...known, card] };
}
